import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Login from "./components/auth-flow/Login.jsx";
import SignUp from "./components/auth-flow/Signup.jsx";
import ForgotPassword from "./components/auth-flow/ForgotPassword.jsx";
import ResetPassword from "./components/auth-flow/ResetPassWord.jsx";
import VerifyCode from "./components/auth-flow/Verification.jsx";
import TwoFactorVerification from "./components/auth-flow/2fa.jsx";
import PaymentSuccess from "./components/PaymentSuccess.jsx";

import "./App.css";
import SelectPackage from "./components/auth-flow/Package.jsx";
import MakePayment from "./components/auth-flow/Payment.jsx";
import DashboardLayout from "./components/dashboard-flow/DashboardLayout.jsx";
import ClientDashboard from "./components/dashboard-flow/ClientDashboard.jsx";
import DashboardWithNoData from "./components/dashboard-flow/DashboardWithNoData.jsx";
import ProtectedRoutes from "./routes/ProtectedRoutes.jsx";
import RoleGate from "./routes/RoleGate.jsx";
import { readCurrentUser } from "./shared/_helpers/currentUser";
import { AuthProvider } from "./context/AuthContext.jsx";
import RouteErrorBoundary from "./routes/RouteErrorBoundary.jsx";
import QuickSetupRouteGuard from "./routes/QuickSetupRouteGuard.jsx";
import OnboardingLayout from "./components/onboarding-flow/OnboardingLayout.jsx";
import QuestionOne from "./components/onboarding-flow/QuestionOne.jsx";
import QuestionTwo from "./components/onboarding-flow/QuestionTwo.jsx";
import QuestionThree from "./components/onboarding-flow/QuestionThree.jsx";
import QuestionFour from "./components/onboarding-flow/QuestionFour.jsx";
import QuestionFive from "./components/onboarding-flow/QuestionFive.jsx";
import QuestionSix from "./components/onboarding-flow/QuestionSix.jsx";
import QuestionSeven from "./components/onboarding-flow/QuestionSeven.jsx";
import Profile from "./components/UserProfile.jsx";
import Documents from "./components/UserDocuments.jsx";
import MeetingList from "./components/UserMeetings.jsx";
import MeetingDetails from "./components/UserMeetingDetails.jsx";
import UserList from "./components/UserList.jsx";
import AdminAdvisorsList from "./components/AdminAdvisorsList.jsx";
import AdvisorMeeting from "./components/AdvisorMeeting";
import AdvisorManageAvailability from "./components/AdvisorManageAvailability";
import AdminDashboard from "./components/dashboard-flow/AdminDashboard.jsx";
import Zoom from "./components/Zoom.jsx";
import Questionnaire from "./components/Questionnaire";
import AdvisorChatScreen from "./components/AdvisorChatScreen.jsx";
import AdminList from "./components/AdminList.jsx";
import InvestmentTool from "./components/InvestmentTool.jsx";
import PackageManagement from "./components/PackageManagement.jsx";
import Welcome from "./components/auth-flow/Welcome.jsx";

function App() {
  return (
    <Router>
      <AuthProvider>
      <RouteErrorBoundary>
      <Routes>
        {/* Client payment flow. RoleGate, not ProtectedRoutes: these must stay
            reachable without a web session (mobile-login payment path). */}
        <Route path="/package" element={<RoleGate allowedRoles={["client"]}><SelectPackage /></RoleGate>} />
        <Route path="/make-payment" element={<RoleGate allowedRoles={["client"]}><MakePayment /></RoleGate>} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-code" element={<VerifyCode />} />
        <Route path="/2fa" element={<TwoFactorVerification />} />
        <Route path="/confirm-payment" element={<RoleGate allowedRoles={["client"]}><PaymentSuccess /></RoleGate>} />
        {/* Onboarding Routes — client-only (SEC-073): Welcome fires
            setQuickSetup on whoever reaches it, so staff must bounce before it
            mounts. */}
        <Route path="/welcome" element={ <ProtectedRoutes allowedRoles={["client"]}><Welcome /></ProtectedRoutes>} />
        <Route
          path="/q1"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <OnboardingLayout>
                <QuestionOne />
              </OnboardingLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/q2"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <QuickSetupRouteGuard>
                <OnboardingLayout>
                  <QuestionTwo />
                </OnboardingLayout>
              </QuickSetupRouteGuard>
            </ProtectedRoutes>
          }
        />
        <Route path="/q3" element={<ProtectedRoutes allowedRoles={["client"]}><QuickSetupRouteGuard><OnboardingLayout><QuestionThree /></OnboardingLayout></QuickSetupRouteGuard></ProtectedRoutes>} />
        <Route path="/q4" element={<ProtectedRoutes allowedRoles={["client"]}><QuickSetupRouteGuard><OnboardingLayout><QuestionFour /></OnboardingLayout></QuickSetupRouteGuard></ProtectedRoutes>} />
        <Route path="/q5" element={<ProtectedRoutes allowedRoles={["client"]}><QuickSetupRouteGuard><OnboardingLayout><QuestionFive /></OnboardingLayout></QuickSetupRouteGuard></ProtectedRoutes>} />
        <Route path="/q6" element={<ProtectedRoutes allowedRoles={["client"]}><QuickSetupRouteGuard><OnboardingLayout><QuestionSix /></OnboardingLayout></QuickSetupRouteGuard></ProtectedRoutes>} />
        <Route path="/q7" element={<ProtectedRoutes allowedRoles={["client"]}><OnboardingLayout><QuestionSeven /></OnboardingLayout></ProtectedRoutes>} />

        {/* Dashboard Routes */}
        {/* /dashboard/:clientId is how staff open a client's detail view;
            /dashboard (no param) is the client's own. Different owners — keep
            them gated separately. */}
        <Route
          path="/dashboard/:clientId"
          element={
            <ProtectedRoutes allowedRoles={["advisor", "admin", "super-admin"]}>
              <DashboardLayout>
                <ClientDashboard />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <ClientDashboard />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoutes allowedRoles={["admin", "super-admin"]}>
              <DashboardLayout>
                <AdminDashboard />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/connect/:id"
          element={
            <ProtectedRoutes>
              <Zoom />
            </ProtectedRoutes>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoutes allowedRoles={["advisor", "admin", "super-admin"]}>
              <DashboardLayout>
                <UserList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/advisors"
          element={
            <ProtectedRoutes allowedRoles={["admin", "super-admin"]}>
              <DashboardLayout>
                <AdminAdvisorsList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
         <Route
          path="/investment-tool"
          element={
            <ProtectedRoutes allowedRoles={["admin", "super-admin"]}>
              <DashboardLayout>
                <InvestmentTool />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/admins"
          element={
            <ProtectedRoutes allowedRoles={["super-admin"]}>
              <DashboardLayout>
                <AdminList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/meeting-details"
          element={
            <ProtectedRoutes>
              <DashboardLayout>
                <MeetingDetails />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/message"
          element={
            <ProtectedRoutes allowedRoles={["advisor"]}>
              <DashboardLayout>
                <AdvisorChatScreen/>
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/dashboard-zero"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <DashboardWithNoData />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        {/* User Routes */}
        <Route
          path="/profile"
          element={
            <ProtectedRoutes>
              <DashboardLayout>
                <Profile />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/documents"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <Documents />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/advisor-manage-availability"
          element={
            <ProtectedRoutes allowedRoles={["advisor"]}>
              <DashboardLayout>
                <AdvisorManageAvailability />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/documents/:clientId"
          element={
            <ProtectedRoutes allowedRoles={["advisor", "admin", "super-admin"]}>
              <DashboardLayout>
                <Documents />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/meetings"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <MeetingList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/meetings/:clientId"
          element={
            <ProtectedRoutes allowedRoles={["advisor", "admin", "super-admin"]}>
              <DashboardLayout>
                <MeetingList />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/advisor-meetings"
          element={
            <ProtectedRoutes allowedRoles={["advisor"]}>
              <DashboardLayout>
                <AdvisorMeeting />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        <Route
          path="/questionnaire"
          element={
            <ProtectedRoutes allowedRoles={["client"]}>
              <DashboardLayout>
                <Questionnaire />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
         <Route
          path="/package-management"
          element={
            <ProtectedRoutes allowedRoles={["super-admin"]}>
              <DashboardLayout>
                <PackageManagement />
              </DashboardLayout>
            </ProtectedRoutes>
          }
        />
        {/* readCurrentUser, not a bare JSON.parse: this expression runs on
            every render of the route table, so a malformed stored value would
            white-screen every URL, not just this one. */}
        <Route
          path="/*"
          element={
            <Navigate
              to={readCurrentUser() ? "/dashboard" : "/login"}
            />
          }
        />
      </Routes>
      </RouteErrorBoundary>
      </AuthProvider>
    </Router >
  );
}

export default App;
